import { ServerCaseItem } from "src/pages/UserDashboard/Cases/api/types";
import { ServerChargebackResource, ServerStream } from "src/types/work-packets";
import { valueOrUndefined } from "src/pages/UserDashboard/Cases/mappers/utils";
import {
  BaseCaseItem,
  CaseItem,
  CaseItemKind,
  ChargebackEscalationCaseItem,
  ShortageEscalationCaseItem,
  ShortageSettlementCaseItem,
} from "src/types/case-items.ts";
import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types";
import { CaseItemKeys } from "src/pages/UserDashboard/Cases/mappers/mapServerToLocalCase";
import { dateOrUndefined } from "src/utils/date-parse";

const getWorkPacketProperties = (item: ServerCaseItem, key: string): string => {
  return (
    item.WORK_PACKET?.WORK_PACKET_PROPERTIES?.filter(prop => prop.KEY === key)
      .map(prop => prop.VALUE)
      .join(", ") || ""
  );
};

export function mapBaseCaseItem<K extends CaseItemKind>(
  item: ServerCaseItem,
  kind: K,
): Omit<BaseCaseItem, "kind"> & { kind: K } {
  return {
    kind,
    [CaseItemKeys.CaseItemId]: item.ID,
    [CaseItemKeys.CaseId]: item.CASE_ID,
    [CaseItemKeys.CaseType]: item.CASE.TYPE,
    [CaseItemKeys.WorkPacketId]: item.WORK_PACKET_ID,
    [CaseItemKeys.ARN]: getWorkPacketProperties(item, "ARN"),
    [CaseItemKeys.ASN]: getWorkPacketProperties(item, "ASN"),
  };
}

function mapServerEscalationItemsToLocalEscalationCaseItem(item: ServerCaseItem): ShortageEscalationCaseItem {
  const vcParentInvoiceId = getWorkPacketProperties(item, "PARENT_INVOICE_NUMBER");

  return {
    ...mapBaseCaseItem(item, "shortage-escalation"),
    [CaseItemKeys.CaseItemStatus]: item.SHORTAGES_ESCALATION_CASE_ITEM?.STATUS || "",
    [CaseItemKeys.DeniedReason]: getWorkPacketProperties(item, "DENIED_REASON") || undefined,
    [CaseItemKeys.VcDisputedInvoiceId]: item.SHORTAGES_OSSR_WORK_PACKET?.DISPUTED_INVOICE_ID || "",
    [CaseItemKeys.VcParentInvoiceId]: vcParentInvoiceId,
    [CaseItemKeys.LastChildInvoiceId]: item.SHORTAGES_OSSR_WORK_PACKET?.DISPUTED_INVOICE_ID || undefined,
    [CaseItemKeys.RemainingOpenBalance]: item.SHORTAGES_OSSR_WORK_PACKET?.REMAINING_BALANCE_OPEN || undefined,
    [CaseItemKeys.OSSRWorkPacketId]: item.SHORTAGES_OSSR_WORK_PACKET?.ID,
    [CaseItemKeys.PoNumber]: item.SHORTAGES_OSSR_WORK_PACKET?.PO_NUMBER || "",
    [CaseItemKeys.ApprovedAmount]: valueOrUndefined(item.SHORTAGES_ESCALATION_CASE_ITEM?.APPROVED_AMOUNT),
    [CaseItemKeys.ShortagesEscalationCaseItemDetailId]: item.SHORTAGES_ESCALATION_CASE_ITEM?.ID || "",
    [CaseItemKeys.ShortagesEscalationCaseDetailsId]: item.CASE?.SHORTAGES_ESCALATION_CASE_DETAILS?.ID || "",
  };
}

function mapServerChargebackEscalationCaseItemToLocalCaseItem(item: ServerCaseItem): ChargebackEscalationCaseItem {
  let financialCharge,
    asin = "",
    po = "";
  if (item.WORK_PACKET.RECOVERY_STREAM === ServerStream.CHARGEBACKS) {
    const workPacketResource = item.WORK_PACKET?.RESOURCE as ServerChargebackResource;
    financialCharge = workPacketResource.FINANCIAL_CHARGE;
    asin = workPacketResource.ASIN_ID;
    po = workPacketResource.VC_PO_ID;
  }

  return {
    ...mapBaseCaseItem(item, "chargeback-escalation"),
    [CaseItemKeys.ChargeBackIssueId]:
      (item.WORK_PACKET?.RESOURCE as ServerChargebackResource).CHARGEBACK_ISSUE_ID || "",
    [CaseItemKeys.FinancialCharge]: financialCharge,
    [CaseItemKeys.CaseItemStatus]: item.CHARGEBACKS_ESCALATION_CASE_ITEM?.STATUS || "",
    [CaseItemKeys.DeniedReason]: getWorkPacketProperties(item, "DENIED_REASON") || undefined,
    [CaseItemKeys.ChargebacksEscalationCaseItemDetailId]: item.CHARGEBACKS_ESCALATION_CASE_ITEM?.ID || "",
    [CaseItemKeys.ASIN]: asin,
    [CaseItemKeys.PoNumber]: po,
    [CaseItemKeys.ApprovedAmount]: item.CHARGEBACKS_ESCALATION_CASE_ITEM?.APPROVED_AMOUNT || undefined,
    [CaseItemKeys.ApprovedDate]:
      dateOrUndefined(item.CHARGEBACKS_ESCALATION_CASE_ITEM?.APPROVED_DATE)?.toDate() || undefined,
  };
}

function mapServerSettlementToLocalCaseItem(item: ServerCaseItem): ShortageSettlementCaseItem {
  return {
    ...mapBaseCaseItem(item, "shortage-settlement"),
    [CaseItemKeys.OSSRWorkPacketId]: item.SHORTAGES_OSSR_WORK_PACKET?.ID,
    [CaseItemKeys.VcDisputedInvoiceId]: item.SHORTAGES_OSSR_WORK_PACKET?.DISPUTED_INVOICE_ID || "",
    [CaseItemKeys.LastChildInvoiceId]: item.SHORTAGES_OSSR_WORK_PACKET?.DISPUTED_INVOICE_ID || "",
    [CaseItemKeys.PoNumber]: item.SHORTAGES_OSSR_WORK_PACKET?.PO_NUMBER || "",
  };
}

export const serverToLocalCaseItemMappers: Record<CaseType, (_: ServerCaseItem) => CaseItem> = {
  [CaseType.SHORTAGE_ESCALATION]: mapServerEscalationItemsToLocalEscalationCaseItem,
  [CaseType.CHARGEBACK_ESCALATION]: mapServerChargebackEscalationCaseItemToLocalCaseItem,
  [CaseType.SHORTAGE_SETTLEMENT]: mapServerSettlementToLocalCaseItem,
};
